import { ElementRef, Injectable } from '@angular/core';
import { PostMessagingService } from '@lst/post-messaging';
import { Observable, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import {
    LstPostMessage,
    LstPostMessageAction,
    LstPostMessageConnection,
    LstPostMessagePayload,
    ResizePagePayload,
} from '../../model/post-message.model';
import { MultiStageAssignmentReportService } from '../../multi-stage-assignment-report/multi-stage-assignment-report.service';

@Injectable({
    providedIn: 'root',
})
export class PostMessageService {
    private childConn$: LstPostMessageConnection;
    private parentConn$: LstPostMessageConnection;
    private readonly parentMessages$ = new Subject<LstPostMessage>();
    private readonly childMessages$ = new Subject<LstPostMessage>();
    public parentPromiseResolver$ = new Subject<LstPostMessage>();
    lastHeightPosted = null;

    constructor(
        private readonly postMessagingService: PostMessagingService,
        private readonly multiStageAssignmentReportService: MultiStageAssignmentReportService
    ) {}

    async createChildPostMessageConnection(contentViewerIframe: ElementRef): Promise<void> {
        try {
            this.childConn$ = await this.postMessagingService.connectToChild(
                contentViewerIframe.nativeElement,
                this.handleChildMessages.bind(this)
            );
        } catch (error) {
            throw new Error('Error occurred in establishing child post message connection');
        }
    }

    async createParentPostMessageConnection(): Promise<void> {
        if (this.multiStageAssignmentReportService.isOpenedInIFrame) {
            try {
                if (!this.parentConn$) {
                    this.parentConn$ = await this.postMessagingService.connectToParent(
                        this.handleParentMessages.bind(this)
                    );
                }
            } catch (error) {
                throw new Error('Error occurred in establishing parent post message connection');
            }
        }
    }

    async handleParentMessages(
        action: LstPostMessageAction,
        payload?: LstPostMessagePayload
    ): Promise<void | LstPostMessage> {
        this.parentMessages$.next({ action, payload });
        if (action === LstPostMessageAction.CLOSE) {
            return this.resolvePostMessagesPromise(this.parentPromiseResolver$);
        }
        return Promise.resolve();
    }

    async handleChildMessages(action: LstPostMessageAction, payload?: LstPostMessagePayload): Promise<void> {
        this.childMessages$.next({ action, payload });
        if (action === LstPostMessageAction.CLOSE) {
            this.multiStageAssignmentReportService.setIsLoading(true);
        }
    }

    async sendMessageToParent(action: LstPostMessageAction, payload?: LstPostMessagePayload): Promise<LstPostMessage> {
        if (this.parentConn$) {
            return  this.parentConn$.sendMessage(action, payload);
        }
        return null;
    }

    async sendMessageToChild(action: LstPostMessageAction, payload?: LstPostMessagePayload): Promise<void | boolean> {
        if (this.childConn$) {
            return this.childConn$.sendMessage(action, payload);
        }
        return null;
    }

    destroyChildMessageConnection(): void {
        if (this.childConn$) {
            this.childConn$.destroy();
        }
    }

    destroyParentPostMessageConnection(): void {
        if (this.parentConn$) {
            this.parentConn$.destroy();
        }
    }

    getParentMessages(): Observable<LstPostMessage> {
        return this.parentMessages$.asObservable();
    }

    getChildMessages(): Observable<LstPostMessage> {
        return this.childMessages$.asObservable();
    }

    sendResizePageMessageToParent(): void {
        const body = document.body,
            html = document.documentElement;

        const height = Math.max(body.scrollHeight, body.offsetHeight, body.clientHeight, html.offsetHeight);

        if (this.lastHeightPosted !== height) {
            const payload: ResizePagePayload = {
                resize_height: height,
            };
            this.sendMessageToParent(LstPostMessageAction.RESIZE_PAGE, payload);
            this.lastHeightPosted = height;
        }
    }

    get isParentPostMessageConnectionCreated(): boolean {
        return !!this.parentConn$;
    }

    private resolvePostMessagesPromise(promiseResolver: Subject<LstPostMessage>): Promise<LstPostMessage> {
        // Resolve promise of messages obtained from assignment viewer post messages connection
        return new Promise((resolve, reject) => {
            promiseResolver.pipe(first()).subscribe((resolvedPromiseData) => resolve(resolvedPromiseData));
        });
    }
}
